import React, { useEffect, useState } from "react";
import DataTable from "../../components/DataTable";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  displayNotification,
  getFormattedDate,
  sendAPIRequest,
} from "../../../helpers";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { USER_TYPE } from "modules/pvs_activities/constants";

const DeniedUsers = ({ isShow }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const authData = useSelector((s) => s.auth.data);
  const [data, setData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const req = await sendAPIRequest(`/api/denied-account-form`);
      const res = await req.json();
      if (res.success) {
        const deniedUsers = res.data.map((user) => {
          const nameParts = user.name.split(" ");
          return {
            id: user.id,
            firstName: nameParts[0] || "",
            lastName: nameParts.slice(1).join(" ") || "",
            email: user.email,
            organisation: user.account_form?.organisation || "",
            createdAt: user.createdAt,
            updatedAt: user.updatedAt,
            deletedAt: user.deletedAt,
            deletedBy: user.terminationLog?.deletedByUser?.name,
            status_id:user.terminationLog?.status_id,
            status_name:
              user.terminationLog?.status_id === 0 ? "Deleted" : "Denied",
            comments:
              user.terminationLog?.reason ?? "No reason provided",
            request_access: user.request_access,
            user_type_name : user.user_type == USER_TYPE.azure_ad_user ? "Azure AD User" : "Local User",
          };
        });
        setData(deniedUsers);
      } else {
        displayNotification("error", `${t("-1", "Error loading data")} [B]`);
      }
      setIsLoading(false);
    } catch (e) {
      console.log(e)
      setIsLoading(false);
      displayNotification("error", `${t("-1", "Error loading data")} [F]`);
    }
  };
  const getBaseUrl = () => {
    return window.location.origin;
  };
  const goBack = () => {
    navigate(-1);
  };

  // Function to handle Undo Delete
  const handleUndoDelete = async (userId) => {
    try {
      setIsLoading(true);
      const req = await sendAPIRequest(`/api/users/undo-delete/${userId}`, 'POST');
      const res = await req.json();
      if (res.success) {
        displayNotification("success", `${t("-1", "User deletion undo successfully")}`);
        loadData();
      } else {
        displayNotification("error", `${t("-1", "Failed to undo deletion")} [B]`);
      }
    } catch (e) {
      console.error(e);
      displayNotification("error", `${t("-1", "Failed to undo deletion")} [F]`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isShow && (
        <div className="header card-woah border border-secondary d-flex px-4 py-2 shadow-sm w-100 my-2">
          <div className="card-title1 lh-1">{t("-1", "Denied User Requests")}</div>
          <div className="header-right-items ms-auto d-flex align-items-center">
            <div className="ms-auto">
              <button onClick={goBack} className="btn btn--primary fs-7 lh-1">
                <i className="fas fa-angle-left text-white" />
                {t("32", "Back")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className=" card card-woah border border-secondary shadow rounded-0">
        <div className="card-body p-4">
          <DataTable
            getRowId={(row) => row.id}
            isLoading={isLoading}
            rows={data}
            className="text-white bg-primary rounded-0"
            columns={[
              {
                field: "firstName",
                headerName: t("-1", "First Name"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 1,
              },
              {
                field: "lastName",
                headerName: t("193", "Last Name"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 1,
              },
              {
                field: "email",
                headerName: t("-1", "Email"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 2,
              },
              {
                field: "user_type_name",
                headerName: t("-1", "Account Type"),
                headerClassName: "text-white bg-primary rounded-0",
                minWidth: 150,
                flex: 2,
              },
              {
                field: "organisation",
                headerName: t("-1", "Organisation"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 2,
              },
              {
                field: "createdAt",
                headerName: t("-1", "Request date"),
                headerClassName: "text-white bg-primary rounded-0",
                valueGetter: ({ value }) => value,
                valueFormatter: ({ value }) => getFormattedDate(value),
                flex: 1.5,
              },
              {
                field: "updatedAt",
                headerName: t("-1", "Refusal Date"),
                headerClassName: "text-white bg-primary rounded-0",
                valueGetter: ({ value }) => value,
                valueFormatter: ({ value }) => getFormattedDate(value),
                flex: 1.5,
              },
              {
                field: "deletedAt",
                headerName: t("-1", "Deletion Date "),
                headerClassName: "text-white bg-primary rounded-0",
                valueGetter: ({ value }) => value,
                valueFormatter: ({ value }) => getFormattedDate(value),
                flex: 1.5,
              },
              {
                field: "deletedBy",
                headerName: t("-1", "Deleted By"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 2,
              },
              {
                field: "status_name",
                headerName: t("-1", "Status"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 2,
              },
              {
                field: "comments",
                headerName: t("-1", "Reason of Deletion"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 2,
              },
              {
                field: "details",
                headerName: t("-1", "Details"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 2,
                renderCell: (params) => {
                  const userId = params.row.id;
                  const baseUrl = getBaseUrl();
                  const query = parseInt(params.row?.status_id) === 0 ? "show_delete=1" : "";
                  if (params.row.request_access == 1) {
                    const userLink = `${baseUrl}/portal/denied-request/${userId}/view?${query}`;
                    return (
                      <a href={userLink} rel="noopener noreferrer">
                        View Details
                      </a>
                    );
                  } else {
                    return (
                      <span
                      role="button"
                        className=""
                        onClick={() => handleUndoDelete(userId)}
                      >
                        Undo Delete
                      </span>
                    );
                  }
                },
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};

export default DeniedUsers;
